import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAppearance } from '../contexts/AppearanceContext';
import {
  announcementDismissKey,
  fetchActiveAnnouncements,
} from '../lib/announcements';
import type { AnnouncementSeverity, PlatformAnnouncement } from '../lib/systemTypes';
import { cn } from '../lib/utils';

function severityStyles(severity: AnnouncementSeverity | undefined) {
  if (severity === 'critical') {
    return {
      border: 'border-red-200/80',
      bg: 'bg-red-50/90',
      text: 'text-red-900',
      icon: AlertTriangle,
    };
  }
  if (severity === 'warning') {
    return {
      border: 'border-amber-200/80',
      bg: 'bg-amber-50/90',
      text: 'text-amber-950',
      icon: AlertTriangle,
    };
  }
  return {
    border: 'border-sky-200/80',
    bg: 'bg-sky-50/90',
    text: 'text-sky-950',
    icon: Info,
  };
}

export default function PlatformAnnouncementBanner() {
  const { user } = useAuth();
  const { isGlass } = useAppearance();
  const [announcements, setAnnouncements] = useState<PlatformAnnouncement[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user?.uid) return;
    void fetchActiveAnnouncements().then(setAnnouncements).catch(() => {});
  }, [user?.uid]);

  const visible = useMemo(() => {
    if (!user?.uid) return null;
    return announcements.find((ann) => {
      if (ann.dismissible === false) return true;
      if (dismissedIds.has(ann.id)) return false;
      return sessionStorage.getItem(announcementDismissKey(user.uid, ann.id)) !== '1';
    });
  }, [announcements, dismissedIds, user?.uid]);

  if (!visible) return null;

  const styles = severityStyles(visible.severity);
  const Icon = styles.icon;
  const canDismiss = visible.dismissible !== false;

  const handleDismiss = () => {
    if (!user?.uid || !canDismiss) return;
    sessionStorage.setItem(announcementDismissKey(user.uid, visible.id), '1');
    setDismissedIds((prev) => new Set(prev).add(visible.id));
  };

  return (
    <div className="app-shell-inset relative z-[44] mb-2 shrink-0">
      <div className="mx-auto w-full max-w-[52rem]">
      <div
        className={cn(
          'w-full px-4 py-3 rounded-2xl border flex items-start gap-3',
          'shadow-[0_8px_30px_-6px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.06)]',
          isGlass ? 'backdrop-blur-xl' : 'backdrop-blur-sm',
          styles.border,
          styles.bg,
        )}
        role="status"
      >
        <Icon className={cn('h-5 w-5 shrink-0 mt-0.5', styles.text)} aria-hidden />
        <div className="flex-1 min-w-0">
          <p className={cn('text-sm font-semibold', styles.text)}>{visible.title}</p>
          <p className={cn('text-sm mt-0.5', styles.text, 'opacity-90')}>{visible.message}</p>
        </div>
        {canDismiss ? (
          <button
            type="button"
            onClick={handleDismiss}
            className={cn(
              'shrink-0 rounded-full p-1 transition-colors hover:bg-black/5',
              styles.text,
            )}
            aria-label="Dismiss announcement"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
    </div>
  );
}
